import { z } from 'zod';
import { baseDocumentFields } from './common.js';

// =============================================================================
// Vertical · a market segment the platform serves (MCMV imobiliário, proteção
// veicular, produção audiovisual, etc).
//
// Each broker belongs to exactly one vertical. The vertical determines:
//   - Form schema for the public qualification flow (form-engine driven)
//   - Qualifier function (pluggable per vertical)
//   - Landing template (which React component renders the public page)
//   - Vocabulary shown in the UI ("Corretor" vs "Parceiro" vs "Vendedor",
//     "Empreendimento" vs "Plano" vs "Pacote")
//   - Default theme tokens (extra layouts and font families per segment)
//
// `key` is the stable identifier referenced from code (e.g. when registering
// templates and qualifier functions). `displayName` is the human-friendly name
// shown in the admin panel.
// =============================================================================

export const verticalKey = z
  .string()
  .min(2)
  .max(40)
  .regex(/^[a-z][a-z0-9-]*$/, 'Use kebab-case lowercase (ex: protecao-veicular)');

export const vertical = z
  .object({
    ...baseDocumentFields,
    /** Stable identifier referenced from code (kebab-case). */
    key: verticalKey,
    /** Human-friendly name shown in the admin panel ("Imobiliário MCMV"). */
    displayName: z.string().trim().min(2).max(80),
    /** Optional short description shown alongside the displayName. */
    description: z.string().max(280).optional(),
    /** Terms used in the UI for this vertical's tenants ("Corretor", "Parceiro", "Vendedor"). */
    brokerTermSingular: z.string().trim().min(2).max(40),
    brokerTermPlural: z.string().trim().min(2).max(40),
    /** Terms used in the UI for what tenants offer ("Empreendimento", "Plano", "Pacote"). */
    listingTermSingular: z.string().trim().min(2).max(40),
    listingTermPlural: z.string().trim().min(2).max(40),
    /** When false, no new brokers can be created under this vertical. */
    isActive: z.boolean().default(true),
  })
  .strict();

export type Vertical = z.infer<typeof vertical>;

/** Stable keys used as defaults from code (avoid magic strings elsewhere). */
export const VERTICAL_KEYS = {
  MCMV_IMOBILIARIO: 'mcmv-imobiliario',
  PROTECAO_VEICULAR: 'protecao-veicular',
  IMOBILIARIO_GERAL: 'imobiliario-geral',
} as const;

export type VerticalKey = (typeof VERTICAL_KEYS)[keyof typeof VERTICAL_KEYS];
